import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Repeat, 
  Calendar, 
  User, 
  Trash2, 
  Edit, 
  Play,
  Clock,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECURRENCE_TYPES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Interval' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const TaskTemplates = () => {
  const { token, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_assignee: '',
    due_offset_days: 7,
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_day: 0,
    recurrence_interval: 7
  });

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/task-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTemplates, fetchUsers]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      default_assignee: '',
      due_offset_days: 7,
      is_recurring: false,
      recurrence_type: 'weekly',
      recurrence_day: 0,
      recurrence_interval: 7
    });
    setEditTemplate(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const handleOpenEdit = (template) => {
    setFormData({
      name: template.name,
      description: template.description || '',
      default_assignee: template.default_assignee || '',
      due_offset_days: template.due_offset_days,
      is_recurring: template.is_recurring,
      recurrence_type: template.recurrence_type || 'weekly',
      recurrence_day: template.recurrence_day || 0,
      recurrence_interval: template.recurrence_interval || 7
    });
    setEditTemplate(template);
    setCreateOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        default_assignee: formData.default_assignee || null
      };

      let response;
      if (editTemplate) {
        response = await fetch(`${API}/task-templates/${editTemplate.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/task-templates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editTemplate ? 'Template updated' : 'Template created');
        fetchTemplates();
        setCreateOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(`${API}/task-templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        setTemplates(prev => prev.filter(t => t.id !== templateId));
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleGenerateTask = async (templateId) => {
    try {
      const response = await fetch(`${API}/task-templates/${templateId}/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Task created from template');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate task');
      }
    } catch (error) {
      toast.error('Failed to generate task');
    }
  };

  const getRecurrenceLabel = (template) => {
    if (!template.is_recurring) return 'One-time';
    
    switch (template.recurrence_type) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return `Weekly on ${DAYS_OF_WEEK.find(d => d.value === template.recurrence_day)?.label || 'Monday'}`;
      case 'biweekly':
        return `Bi-weekly on ${DAYS_OF_WEEK.find(d => d.value === template.recurrence_day)?.label || 'Monday'}`;
      case 'monthly':
        return `Monthly on day ${template.recurrence_day || 1}`;
      case 'custom':
        return `Every ${template.recurrence_interval} days`;
      default:
        return 'Recurring';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse text-muted-foreground text-center">
            Loading templates...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Task Templates
        </CardTitle>
        <Button variant="outline" size="sm" onClick={handleOpenCreate} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </CardHeader>
      <CardContent>
        {templates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="mb-2">No templates yet</p>
            <p className="text-sm">Create templates for recurring tasks</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 rounded-xl border bg-card transition-all duration-200 hover:bg-muted/50 hover:shadow-md hover:border-primary/30"
                  data-testid={`template-card-${template.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{template.name}</h3>
                        {template.is_recurring && (
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400">
                            <Repeat className="w-3 h-3 mr-1" />
                            Recurring
                          </Badge>
                        )}
                        {!template.is_active && (
                          <Badge variant="outline" className="bg-muted text-muted-foreground">
                            Paused
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {template.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Due in {template.due_offset_days} days
                        </span>
                        {template.default_assignee_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {template.default_assignee_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {getRecurrenceLabel(template)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleGenerateTask(template.id)}
                        title="Create task now"
                        data-testid={`generate-task-${template.id}`}
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenEdit(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(template.id)}
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) resetForm(); setCreateOpen(open); }}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Create a template for tasks that repeat regularly
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Weekly Status Report"
                data-testid="template-name-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
                data-testid="template-description-input"
              />
            </div>

            {/* Default Assignee */}
            <div className="space-y-2">
              <Label>Default Assignee</Label>
              <Select
                value={formData.default_assignee || "__self__"}
                onValueChange={(value) => setFormData({ ...formData, default_assignee: value === "__self__" ? "" : value })}
              >
                <SelectTrigger data-testid="template-assignee-select">
                  <SelectValue placeholder="Self (creator)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">Self (creator)</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} {u.id === user?.id && '(You)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Offset */}
            <div className="space-y-2">
              <Label htmlFor="due-offset">Due Date Offset (days)</Label>
              <Input
                id="due-offset"
                type="number"
                min={1}
                max={365}
                value={formData.due_offset_days}
                onChange={(e) => setFormData({ ...formData, due_offset_days: parseInt(e.target.value) || 7 })}
                data-testid="template-offset-input"
              />
              <p className="text-xs text-muted-foreground">
                Task will be due this many days after creation
              </p>
            </div>

            {/* Recurring Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <Repeat className="w-5 h-5 text-blue-500" />
                <div>
                  <Label htmlFor="is-recurring" className="cursor-pointer">Recurring Task</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically create tasks on a schedule
                  </p>
                </div>
              </div>
              <Switch
                id="is-recurring"
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                data-testid="template-recurring-switch"
              />
            </div>

            {/* Recurrence Options */}
            {formData.is_recurring && (
              <div className="space-y-4 p-3 rounded-lg border bg-muted/30">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={formData.recurrence_type}
                    onValueChange={(value) => setFormData({ ...formData, recurrence_type: value })}
                  >
                    <SelectTrigger data-testid="template-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Day of Week (for weekly/biweekly) */}
                {(formData.recurrence_type === 'weekly' || formData.recurrence_type === 'biweekly') && (
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={String(formData.recurrence_day)}
                      onValueChange={(value) => setFormData({ ...formData, recurrence_day: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="template-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day) => (
                          <SelectItem key={day.value} value={String(day.value)}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Day of Month (for monthly) */}
                {formData.recurrence_type === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Day of Month</Label>
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      value={formData.recurrence_day || 1}
                      onChange={(e) => setFormData({ ...formData, recurrence_day: parseInt(e.target.value) || 1 })}
                      data-testid="template-month-day-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use 1-28 to ensure the day exists in all months
                    </p>
                  </div>
                )}

                {/* Custom Interval */}
                {formData.recurrence_type === 'custom' && (
                  <div className="space-y-2">
                    <Label>Every X Days</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={formData.recurrence_interval}
                      onChange={(e) => setFormData({ ...formData, recurrence_interval: parseInt(e.target.value) || 7 })}
                      data-testid="template-interval-input"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setCreateOpen(false); }} data-testid="template-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="template-save-btn">
              {editTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TaskTemplates;
